import React, { useEffect, useRef, useState } from 'react';
import { confirmDialog } from './Notifications';
import { useTheme } from '../context/ThemeContext';
import { CloseIcon } from './icons';
import { useProjects } from '../context/ProjectContext';
import {
  deleteProject,
  deleteProjectFile,
  getProject,
  Project,
  updateProject,
  uploadProjectFile,
} from '../services/api';

interface Props {
  projectId: string;
  onClose: () => void;
}

const ProjectSettingsModal: React.FC<Props> = ({ projectId, onClose }) => {
  const { colors } = useTheme();
  const { refreshProjects, activeProjectId, selectProject } = useProjects();
  const [project, setProject] = useState<Project | null>(null);
  const [name, setName] = useState('');
  const [memoryEnabled, setMemoryEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await getProject(projectId);
        if (cancelled) return;
        setProject(p);
        setName(p.name);
        setMemoryEnabled(p.memory_enabled);
      } catch (err: any) {
        if (!cancelled) setError('Failed to load project');
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const save = async () => {
    if (!project) return;
    const wipeWarning =
      project.memory_enabled && !memoryEnabled
        ? 'Turning off memory will permanently wipe the stored project memory. Continue?'
        : null;
    if (wipeWarning && !(await confirmDialog(wipeWarning, { confirmLabel: 'Wipe memory', danger: true }))) return;
    try {
      setBusy(true);
      const updated = await updateProject(projectId, {
        name: name.trim() || project.name,
        memory_enabled: memoryEnabled,
      });
      setProject(updated);
      await refreshProjects();
    } catch (err) {
      setError('Save failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!project) return;
    if (!(await confirmDialog(`Delete project "${project.name}" and all its conversations?`, { confirmLabel: 'Delete', danger: true }))) return;
    try {
      setBusy(true);
      await deleteProject(projectId);
      if (activeProjectId === projectId) {
        await selectProject(null);
      }
      await refreshProjects();
      onClose();
    } catch {
      setError('Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const handleUpload = async (files: FileList | null) => {
    if (!files || !project) return;
    try {
      setBusy(true);
      let current = project;
      for (const f of Array.from(files)) {
        current = await uploadProjectFile(projectId, f);
      }
      setProject(current);
      await refreshProjects();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeFile = async (blobName: string) => {
    if (!project) return;
    if (!(await confirmDialog('Remove this file from the project?', { confirmLabel: 'Remove', danger: true }))) return;
    try {
      setBusy(true);
      await deleteProjectFile(projectId, blobName);
      const fresh = await getProject(projectId);
      setProject(fresh);
      await refreshProjects();
    } catch {
      setError('Remove failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '560px', maxWidth: '92vw', maxHeight: '85vh', overflow: 'auto',
          backgroundColor: colors.surface, color: colors.textPrimary,
          border: `1px solid ${colors.border}`, borderRadius: '12px',
          padding: '20px', fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Project settings</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'transparent', border: 'none', color: colors.textMuted, cursor: 'pointer', display: 'flex', padding: '4px' }}
          ><CloseIcon size={18} /></button>
        </div>

        {!project ? (
          <div style={{ padding: '24px 0', color: colors.textMuted }}>Loading…</div>
        ) : (
          <>
            <label style={{ display: 'block', marginTop: '16px', fontSize: '12px', color: colors.textMuted }}>Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{
                width: '100%', padding: '8px 10px', marginTop: '4px',
                borderRadius: '6px', border: `1px solid ${colors.border}`,
                backgroundColor: colors.surfaceElevated, color: colors.textPrimary,
                boxSizing: 'border-box',
              }}
            />

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '16px' }}>
              <input
                type="checkbox"
                checked={memoryEnabled}
                onChange={(e) => setMemoryEnabled(e.target.checked)}
              />
              <span>Remember things across chats in this project</span>
            </label>
            <div style={{ marginTop: '6px', fontSize: '12px', color: colors.textMuted }}>
              When enabled, short facts are distilled after each turn and injected into future chats in this project.
              Turning this off wipes the stored memory.
            </div>

            <div style={{ marginTop: '20px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Project files</div>
              <div style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '8px' }}>
                Documents available in every conversation inside this project. Caps: 5 documents, 50&nbsp;MB total. Images are not supported.
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.md,.json,.html,.htm,.rtf"
                onChange={(e) => handleUpload(e.target.files)}
                disabled={busy}
              />
              <div style={{ marginTop: '8px' }}>
                {project.files.length === 0 ? (
                  <div style={{ color: colors.textMuted, fontSize: '12px' }}>No files yet.</div>
                ) : (
                  project.files.map((f) => (
                    <div
                      key={f.blob_name}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '6px 8px', borderBottom: `1px solid ${colors.borderLight}`,
                      }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.filename} <span style={{ color: colors.textMuted, fontSize: '11px' }}>({Math.round(f.size / 1024)} KB)</span>
                      </span>
                      <button
                        onClick={() => removeFile(f.blob_name)}
                        disabled={busy}
                        style={{
                          background: 'transparent', border: 'none', color: colors.textMuted,
                          cursor: 'pointer', fontSize: '13px',
                        }}
                      >Remove</button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {error && (
              <div style={{ marginTop: '12px', color: '#b94a4a', fontSize: '12px' }}>{error}</div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
              <button
                onClick={handleDelete}
                disabled={busy}
                style={{
                  background: 'transparent', border: `1px solid ${colors.border}`,
                  color: '#b94a4a', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer',
                }}
              >Delete project</button>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={onClose}
                  disabled={busy}
                  style={{
                    background: 'transparent', border: `1px solid ${colors.border}`,
                    color: colors.textPrimary, padding: '8px 12px', borderRadius: '6px', cursor: 'pointer',
                  }}
                >Cancel</button>
                <button
                  onClick={save}
                  disabled={busy}
                  style={{
                    background: colors.primary, border: 'none', color: '#fff',
                    padding: '8px 14px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600,
                  }}
                >Save</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectSettingsModal;
